export const SUB_STEP_MAPPINGS = {
  MURCIA: {
    1: "Push to object",
    2: "Equipment emptying",
    3: "Push to CIP",
    4: "Cleaning",
    5: "Equipment emptying",
    6: "Equipment drain",
    7: "Final rinse",
  },
  SANDSTON: {
    1: "Wash Tank Fill",
    2: "Sanitize Tank Fill",
    3: "Rinse Tank Fill",
    4: "Wash Tank Drain",
    5: "Sanitize Tank Drain",
    6: "Rinse Tank Drain",
    7: "Fresh Water Rinse Charge to Circuit Tank",
    8: "Fresh Water Rinse to Drain",
    9: "Pre-Rinse Charge to Circuit Tank",
    10: "Pre-Rinse Charge to Drain (Proof of Return)",
    11: "Caustic Addition and Heating",
    12: "Caustic Charge to Circuit Tank",
    14: "Caustic Charge to Drain",
    15: "Caustic Recirculation",
    16: "Caustic Cooldown",
    17: "Post Chemical Rinse to Caustic Tank",
    18: "Sanitizer Addition",
    19: "Sanitizer Recirculation",
    20: "Hot Sanitize Heating",
    21: "Hot Sanitize Recirculation",
    22: "Cooldown",
    23: "Air Purge",
    24: "Circuit Return Air Purge",
    25: "Circuit Tank Pump Out to Drain",
    26: "Circuit Tank Pump Out to Caustic Tank",
    27: "Drain and Vent",
    28: "CIP Complete",
    30: "Manual Step",
    31: "Post Chemical Rinse to Drain",
    32: "Caustic Heating Recirculation (Skid Only)",
    33: "Sanitizer to Drain Until Empty (No Return)",
    34: "Sanitizer Soak",
    35: "Sanitizer Charge to Drain",
    36: "Sanitizer Charge to Circuit Tank",
    37: "Fill Tanks",
    38: "Air Blow And Pump Back From Equipment",
    39: "Rinse Tank to Drain Until Empty",
    40: "Pre-Heat For Cooldown",
    41: "Air Blow And Wash Water Recovery",
    42: "Pump Back From Equipment",
    43: "Cooldown Rinse",
    44: "Pre-Rinse to Drain to Level Setpoint",
    45: "Pre-Rinse to Drain Until Empty",
    46: "Pre-Rinse Tank Fill",
    47: "Pre-Rinse Tank Drain (Skid Only)",
    49: "Wash Tank Rinse (Skid Only)",
    50: "Transition Forward Flow / Recirculate",
    51: "Rinse Tank to Drain (Skid Only)",
    52: "Filler Final Rinse to Drain",
  },
  DEFAULT: {
    1: "Sub-step 1",
    2: "Sub-step 2",
    3: "Sub-step 3",
    4: "Sub-step 4",
    5: "Sub-step 5",
    6: "Sub-step 6",
    7: "Sub-step 7",
    8: "Sub-step 8",
    9: "Sub-step 9",
    10: "Sub-step 10",
  },
};

// Site detection - adjust these conditions based on your actual data
export const PRODUCTION_LINE_TO_SITE = {
  // Murcia production lines
  murcia_cip_gea: "MURCIA",

  // Sandston production lines
  sandston_cip: "SANDSTON",
};

// Configuration for which series should show sub-step details in tooltips
export const STEP_SERIES_WITH_SUBSTEP_INFO = {
  MURCIA: ["Supply Step", "Return Step", "Line 1 Step", "Line 2 Step"],
  SANDSTON: ["Line 1 Step", "Line 2 Step", "Line 5 Step"],
  DEFAULT: ["Supply Step", "Return Step"], // Default series that show substeps
};

// Configuration for return step series names per site
export const RETURN_STEP_SERIES_CONFIG = {
  MURCIA: ["Supply Step"],
  SANDSTON: ["Line 1 Step", "Line 2 Step", "Line 5 Step"],
  DEFAULT: [], // Track all return steps by default
};

/**
 * Get the correct sub-step mapping based on site/line
 * @param {string|number} productionLineId - Production line ID
 * @param {string} lineName - Line name
 * @param {string} companyName - Company name from env
 * @returns {Object} Sub-step mapping object
 */
/**
 * Get the correct sub-step mapping based on production line
 * @param {Object} selectedProductionLine - The production line object with id
 * @returns {Object} Sub-step mapping object
 */
export const getSubStepMapping = (selectedProductionLine) => {
  // Get the production line ID
  const productionLineId =
    selectedProductionLine?.id ||
    selectedProductionLine?.name ||
    (typeof selectedProductionLine === "string"
      ? selectedProductionLine
      : null);

  if (!productionLineId) {
    console.warn("No production line ID found, using DEFAULT mapping");
    return SUB_STEP_MAPPINGS.DEFAULT;
  }

  // Look up the site for this production line
  const site = PRODUCTION_LINE_TO_SITE[productionLineId];

  if (site && SUB_STEP_MAPPINGS[site]) {
    return SUB_STEP_MAPPINGS[site];
  }

  // Fallback: check if the ID contains site keywords
  const lineIdLower = productionLineId.toString().toLowerCase();
  if (lineIdLower.includes("sandston")) {
    return SUB_STEP_MAPPINGS.SANDSTON;
  }
  if (lineIdLower.includes("murcia")) {
    return SUB_STEP_MAPPINGS.MURCIA;
  }

  console.warn(
    `Unknown production line: ${productionLineId}, using DEFAULT mapping`
  );
  return SUB_STEP_MAPPINGS.DEFAULT;
};
/**
 * Parse step name and format with sub-step info
 * @param {string} stepName
 * @param {Object} subStepMapping - Mapping object from SUB_STEP_MAPPINGS
 * @returns {Object|null} Parsed sub-step information
 */
export const formatSubStepInfo = (stepName, subStepMapping) => {
  const match = stepName.match(/io_step_(\d+)seq(\d+)/);
  if (!match) return null;

  const subStepNumber = parseInt(match[1]);
  const sequenceNumber = match[2];
  const subStepName =
    subStepMapping[subStepNumber] || `Sub-step ${subStepNumber}`;

  return {
    subStepNumber: subStepNumber,
    sequenceNumber: sequenceNumber,
    subStepName: subStepName,
    formatted: `${subStepNumber} - ${subStepName}`, // "1 - Push to object"
  };
};

/**
 * Get the series names that should show sub-step info in tooltips
 * @param {Object} selectedProductionLine - The production line object with id
 * @returns {Array} Array of series names that should show substep details
 */
export const getStepSeriesWithSubstepInfo = (selectedProductionLine) => {
  // Get the production line ID
  const productionLineId =
    selectedProductionLine?.id ||
    selectedProductionLine?.name ||
    (typeof selectedProductionLine === "string"
      ? selectedProductionLine
      : null);

  if (!productionLineId) {
    return STEP_SERIES_WITH_SUBSTEP_INFO.DEFAULT;
  }

  // Look up the site for this production line
  const site = PRODUCTION_LINE_TO_SITE[productionLineId];

  if (site && STEP_SERIES_WITH_SUBSTEP_INFO[site]) {
    return STEP_SERIES_WITH_SUBSTEP_INFO[site];
  }

  // Fallback: check if the ID contains site keywords
  const lineIdLower = productionLineId.toString().toLowerCase();
  if (lineIdLower.includes("sandston")) {
    return STEP_SERIES_WITH_SUBSTEP_INFO.SANDSTON;
  }
  if (lineIdLower.includes("murcia")) {
    return STEP_SERIES_WITH_SUBSTEP_INFO.MURCIA;
  }

  return STEP_SERIES_WITH_SUBSTEP_INFO.DEFAULT;
};

/**
 * Get the return step series names to track based on production line
 * @param {Object} selectedProductionLine - The production line object with id
 * @returns {Array|null} Array of series names to track, or null to track all
 */
export const getReturnStepSeriesToTrack = (selectedProductionLine) => {
  // Get the production line ID
  const productionLineId =
    selectedProductionLine?.id ||
    selectedProductionLine?.name ||
    (typeof selectedProductionLine === "string"
      ? selectedProductionLine
      : null);

  if (!productionLineId) {
    console.warn("No production line ID found, tracking all return steps");
    return null; // Track all
  }

  // Look up the site for this production line
  const site = PRODUCTION_LINE_TO_SITE[productionLineId];

  if (site && RETURN_STEP_SERIES_CONFIG[site]) {
    return RETURN_STEP_SERIES_CONFIG[site];
  }

  // Fallback: check if the ID contains site keywords
  const lineIdLower = productionLineId.toString().toLowerCase();
  if (lineIdLower.includes("sandston")) {
    return RETURN_STEP_SERIES_CONFIG.SANDSTON;
  }
  if (lineIdLower.includes("murcia")) {
    return RETURN_STEP_SERIES_CONFIG.MURCIA;
  }

  console.warn(
    `Unknown production line: ${productionLineId}, tracking all return steps`
  );
  return null;
};

/**
 * Convert Sandston API response to substep mapping
 * @param {Array} stepPairs - Array of { seq_step_id, step_id }
 * @returns {Object} Mapping from seq_step_id to step name
 */
export const createSandstonDynamicMapping = (stepPairs) => {
  const mapping = {};

  stepPairs.forEach((pair) => {
    // Use the step_id to look up the step name from static SANDSTON mapping
    const stepName = SUB_STEP_MAPPINGS.SANDSTON[pair.step_id];
    if (stepName) {
      mapping[pair.seq_step_id] = stepName;
    }
  });

  return mapping;
};

/**
 * Parse step name for Sandston (uses sequence number, not step number)
 * @param {string} stepName - e.g., "io_step_02seq105"
 * @param {Object} dynamicMapping - Mapping from sequence to step name
 * @returns {Object|null} Parsed sub-step information
 */
export const formatSandstonSubStepInfo = (stepName, dynamicMapping) => {
  const match = stepName.match(/io_step_(\d+)seq(\d+)/);
  if (!match) return null;

  const stepNumber = parseInt(match[1]);
  const sequenceNumber = parseInt(match[2]);

  // For Sandston, use the sequence number to look up the step name
  const subStepName =
    dynamicMapping && dynamicMapping[sequenceNumber]
      ? dynamicMapping[sequenceNumber]
      : `Sequence ${sequenceNumber}`;

  return {
    stepNumber: stepNumber,
    sequenceNumber: sequenceNumber,
    subStepName: subStepName,
    formatted: `${sequenceNumber} - ${subStepName}`,
  };
};
